class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // A page restored from the back/forward cache must not come back with the drawer open and the
    // page scroll-locked.
    window.addEventListener('pagehide', () => {
      if (this.classList.contains('active') || this.openTimer) this.close();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active') || this.openTimer) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Focus is part of opening, not animation completion: a reduced-motion
    // preference, interrupted transition, or first paint can omit transitionend.
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.classList.add('animate', 'active');
      const focusDeadline = performance.now() + 1500;
      const focusWhenVisible = () => {
        this.openFocusFrame = null;
        if (!this.isConnected || !this.classList.contains('active')) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const targets = getFocusableElements(containerToTrapFocusOn);
        // visibility itself transitions in Dawn. The first activation tick can
        // still be hidden; wait for a painted visible control, not transitionend.
        const focusElement = targets.find(
          (element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
        );
        if (focusElement) {
          // Start on the trap's first control, so initial Shift+Tab is contained.
          trapFocus(containerToTrapFocusOn, focusElement);
        } else if (targets.length && performance.now() < focusDeadline) {
          this.openFocusFrame = requestAnimationFrame(focusWhenVisible);
        } else {
          // A custom style must not leave a permanent animation-frame loop.
          const dialog = this.querySelector('.drawer__inner');
          if (dialog && dialog.getClientRects().length && getComputedStyle(dialog).visibility !== 'hidden') {
            trapFocus(dialog, dialog);
          }
        }
      };
      this.openFocusFrame = requestAnimationFrame(focusWhenVisible);
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    // dispatchViewEvent is absent when Shopify's standard-events.js failed to load (cart.js falls
    // back to a plain element); opening the bag must not throw because of that.
    const drawerItems = this.querySelector('cart-drawer-items');
    if (drawerItems && typeof drawerItems.dispatchViewEvent === 'function') drawerItems.dispatchViewEvent();
  }

  close() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    cancelAnimationFrame(this.openFocusFrame);
    this.openFocusFrame = null;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // The AP cart skin keys its empty-state CSS off `cart-drawer.is-empty` (the host element),
    // but that class is only set by Liquid at page load — so after an AJAX add/remove the host
    // must be re-synced, or the empty-state grid collapses a now-populated cart to 0px (and vice
    // versa). Derive emptiness from the freshly rendered DOM rather than the add response.
    this.classList.toggle('is-empty', !this.querySelector('.cart-item'));

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
